import { Dialog, Transition } from "@headlessui/react"
import React, { Fragment, useMemo } from "react"

import useToggleState from "@lib/hooks/use-toggle-state"
import { ChevronDown, X, Loader2 } from "lucide-react"

import { getProductPrice } from "@lib/util/get-product-price"
import OptionSelect from "./option-select"
import { HttpTypes } from "@medusajs/types"
import { isSimpleProduct } from "@lib/util/product"

type MobileActionsProps = {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
  options: Record<string, string | undefined>
  updateOptions: (title: string, value: string) => void
  inStock?: boolean
  allOutOfStock?: boolean
  handleAddToCart: () => void
  isAdding?: boolean
  show: boolean
  optionsDisabled: boolean
}

const MobileActions: React.FC<MobileActionsProps> = ({
  product,
  variant,
  options,
  updateOptions,
  inStock,
  allOutOfStock,
  handleAddToCart,
  isAdding,
  show,
  optionsDisabled,
}) => {
  const { state, open, close } = useToggleState()

  const price = getProductPrice({
    product: product,
    variantId: variant?.id,
  })

  const selectedPrice = useMemo(() => {
    if (!price) {
      return null
    }
    const { variantPrice, cheapestPrice } = price

    return variantPrice || cheapestPrice || null
  }, [price])

  const isSimple = isSimpleProduct(product)

  return (
    <>
      <div
        className={`lg:hidden inset-x-0 bottom-0 fixed z-50 ${
          !show ? "pointer-events-none" : ""
        }`}
      >
        <Transition
          as={Fragment}
          show={show}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="bg-white flex flex-col gap-y-3 justify-center items-center p-4 h-full w-full border-t border-gray-200 shadow-lg"
            data-testid="mobile-actions"
          >
            <div className="flex items-center gap-x-2">
              <span className="text-sm font-medium text-grey-90" data-testid="mobile-title">
                {product.title}
              </span>
              <span className="text-grey-30">—</span>
              {selectedPrice ? (
                <div className="flex items-end gap-x-2 text-grey-90">
                  {selectedPrice.price_type === "sale" && (
                    <p>
                      <span className="line-through text-grey-40 text-sm">
                        {selectedPrice.original_price}
                      </span>
                    </p>
                  )}
                  <span
                    className={`text-sm font-bold ${
                      selectedPrice.price_type === "sale"
                        ? "text-brand-accent"
                        : ""
                    }`}
                  >
                    {selectedPrice.calculated_price}
                  </span>
                </div>
              ) : (
                <div></div>
              )}
            </div>
            <div className={`grid w-full gap-x-4 ${
              isSimple ? "grid-cols-1" : "grid-cols-2"
            }`}>
              {!isSimple && (
                <button
                  onClick={open}
                  className="w-full h-12 rounded-lg border border-brand-primary text-brand-primary font-medium hover:bg-brand-primary/5 transition-colors flex items-center justify-center"
                  data-testid="mobile-actions-button"
                >
                  <div className="flex items-center justify-between w-full px-4">
                    <span className="text-sm">
                      {variant
                        ? Object.values(options).join(" / ")
                        : "Select Options"}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>
              )}
              <button
                onClick={handleAddToCart}
                disabled={allOutOfStock || (!!variant && !inStock) || isAdding}
                className="w-full h-12 rounded-lg bg-brand-primary text-white font-medium hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="mobile-cart-button"
              >
                {isAdding && <Loader2 className="w-4 h-4 animate-spin" />}
                {allOutOfStock || (!!variant && !inStock)
                  ? "Out of stock"
                  : "Add to cart"}
              </button>
            </div>
          </div>
        </Transition>
      </div>
      <Transition appear show={state} as={Fragment}>
        <Dialog as="div" className="relative z-[75]" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-700 bg-opacity-75 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed bottom-0 inset-x-0">
            <div className="flex min-h-full h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Panel
                  className="w-full h-full transform overflow-hidden text-left flex flex-col gap-y-3"
                  data-testid="mobile-actions-modal"
                >
                  <div className="w-full flex justify-end pr-6">
                    <button
                      onClick={close}
                      className="bg-white w-12 h-12 rounded-full text-grey-90 flex justify-center items-center shadow-md"
                      data-testid="close-modal-button"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="bg-white px-6 py-12 rounded-t-2xl">
                    {(product.variants?.length ?? 0) > 1 && (
                      <div className="flex flex-col gap-y-6">
                        {(product.options || []).map((option) => {
                          return (
                            <div key={option.id}>
                              <OptionSelect
                                option={option}
                                current={options[option.id]}
                                updateOption={updateOptions}
                                title={option.title ?? ""}
                                disabled={optionsDisabled}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default MobileActions
